/**
 * Closed enums for the `pwa_logs` telemetry spine.
 *
 * Read `docs/telemetry/PLAN.md` before changing anything here. The short
 * version of the rules that matter:
 *
 * 1. Event names are ASCII `[a-z0-9._]` only — no spaces, no em-dashes, no
 *    smart quotes. Firestore has no substring match, so `event` is queried by
 *    exact equality, and the runbooks playbook has a standing warning about
 *    U+2014 silently zeroing out exact-match queries on `message`. Restricting
 *    the character set is what structurally removes that trap.
 * 2. NEVER rename an event. Every historical query, and every `signature_query`
 *    recorded in a `truetoform_runbooks` bug dossier, is pinned to the exact
 *    string. To change meaning, add a new name and mark the old one deprecated
 *    in `docs/telemetry/EVENTS.md`.
 * 3. Adding a value here REQUIRES an entry in `docs/telemetry/EVENTS.md` in the
 *    same commit. An event with no documented payload and no documented
 *    "what a bad value looks like" cannot be interpreted by an analysis agent.
 */

/**
 * Funnel steps. `phase.enter` for a phase is the denominator for every rate
 * measured inside it — see PLAN.md §6.
 */
export const Phases = {
  BOOT: "boot",
  ASSETS: "assets",
  PERMISSIONS: "permissions",
  SCAN_SETUP: "scan_setup",
  ALIGNMENT: "alignment",
  ROTATION: "rotation",
  UPLOAD: "upload",
  FORM: "form",
  SUBMIT: "submit",
  SHARE: "share",
} as const;

export type Phase = (typeof Phases)[keyof typeof Phases];

/** How a phase ended. Only ever set on `phase.exit` and `*.summary` rows. */
export const Outcomes = {
  /** Moved forward to the next phase — the success case. */
  ADVANCED: "advanced",
  /** User left: pagehide, back-nav, or a route change out of the phase. */
  ABANDONED: "abandoned",
  /** An explicit error state rendered. */
  ERROR: "error",
  /** Tab hidden and never restored before unload. */
  BACKGROUNDED: "backgrounded",
  /** A watchdog fired. */
  TIMEOUT: "timeout",
} as const;

export type Outcome = (typeof Outcomes)[keyof typeof Outcomes];

/**
 * Deployment tenant. Derived from the build-time `REACT_APP_IS_*_ENV` flags —
 * see `resolveTenant()` in `./context.ts`. Note this is a superset of the four
 * tenants named in CLAUDE.md: `sisley` and `stylematch` were added later.
 */
export const Tenants = {
  MAIN: "main",
  MALCHUS: "malchus",
  NEEMS: "neems",
  SISLEY: "sisley",
  STYLEMATCH: "stylematch",
  /** `ttf-pwa-test` — a separate hosting target inside the DEV Firebase project. */
  TEST: "test",
  DEV: "dev",
} as const;

export type Tenant = (typeof Tenants)[keyof typeof Tenants];

/**
 * Typed events. Stage 1 ships only the ones the logger itself emits; the phase
 * model (Stage 2) and the struggle summaries (Stage 3) extend this.
 *
 * Namespacing is `<area>.<subject>.<verb>`.
 */
export const Events = {
  /** A page load attached itself to a journey. First row of every page load. */
  APP_PAGELOAD: "app.pageload",
  /** Device / capability snapshot, once per page load. Stage 2. */
  APP_ENVIRONMENT: "app.environment",
  /** Rows recovered from the localStorage outbox after a page death. Stage 3. */
  APP_OUTBOX_DRAINED: "app.outbox.drained",

  /** Entered a funnel phase. The denominator for that phase. Stage 2. */
  PHASE_ENTER: "phase.enter",
  /** Left a funnel phase, with `outcome` and `durationMs`. Stage 2. */
  PHASE_EXIT: "phase.exit",

  /** Aggregated alignment struggle record. Stage 3. */
  SCAN_ALIGNMENT_SUMMARY: "scan.alignment.summary",
  /** Aggregated rotation record. Stage 3. */
  SCAN_ROTATION_SUMMARY: "scan.rotation.summary",
  /** Aggregated upload record. Stage 3. */
  SCAN_UPLOAD_SUMMARY: "scan.upload.summary",

  /**
   * The startup device check's verdict — speed AND keypoint accuracy on the
   * shipped real frames. Once per page load whose check ran.
   */
  DEVICE_BENCHMARK: "device.benchmark",
  /** Dev only: a known-healthy device calibrating the benchmark (/dev/benchmark). */
  BENCHMARK_CALIBRATION: "benchmark.calibration",

  /**
   * A visit to an avatars list had its list: from memory at once, or from the
   * server. Once per visit. BUG-0014.
   */
  LIST_SCANS_RESOLVED: "list.scans.resolved",
  /** A visit with nothing cached gave up waiting for the server. BUG-0014. */
  LIST_SCANS_TIMEOUT: "list.scans.timeout",
  /** A visit with nothing cached had its load fail. BUG-0014. */
  LIST_SCANS_ERROR: "list.scans.error",
  /** The listener on one just-submitted scan died before the scan finished. */
  LIST_SCAN_WATCH_ERROR: "list.scan.watch.error",
} as const;

export type Event = (typeof Events)[keyof typeof Events];

/**
 * Legacy bracket-prefix → event slug.
 *
 * The 243 pre-existing `logInfo`/`logWarning`/`logError` call sites are NOT
 * being migrated in Stage 1 (PLAN.md §10 — zero call-site changes). They still
 * need *some* `event` value or composite index #2 (`event`, `serverTs`) is
 * useless for them.
 *
 * This map is deliberately CLOSED rather than derived by normalising whatever
 * appears between the brackets. A derived slug would let one dynamic prefix
 * (`[scan-abc123]`) blow up the cardinality of an indexed field, which is the
 * one thing PLAN.md §4.4 says never to do. Anything not listed here lands in
 * `legacy.other`, which is a bounded, honest answer.
 *
 * All 17 prefixes present in `src/` as of v2.5.7 are listed. When you add a new
 * prefixed log line, add the prefix here too — otherwise it silently joins the
 * `legacy.other` bucket and stops being separable by query.
 */
export const LEGACY_PREFIX_EVENTS: { [prefix: string]: string } = {
  App: "legacy.app",
  AvatarSelect: "legacy.avatarselect",
  DeviceMotion: "legacy.devicemotion",
  DiscardModal: "legacy.discardmodal",
  NewAvatarScreen: "legacy.newavatarscreen",
  RotationError: "legacy.rotationerror",
  RotationManager: "legacy.rotationmanager",
  ScanRecovery: "legacy.scanrecovery",
  ScanUploader: "legacy.scanuploader",
  ScanningScreen: "legacy.scanningscreen",
  SubmissionDiscardModal: "legacy.submissiondiscardmodal",
  SubmitScan: "legacy.submitscan",
  auth: "legacy.auth",
  avatarSharing: "legacy.avatarsharing",
  isStationary: "legacy.isstationary",
  "magic-link": "legacy.magiclink",
  useCamera: "legacy.usecamera",
  widgetSession: "legacy.widgetsession",
};

/** Bucket for prefixed lines whose prefix isn't in the map, and for unprefixed lines. */
export const LEGACY_OTHER_EVENT = "legacy.other";

/**
 * Matches a leading `[Prefix]`. Bounded at 40 chars so a pathological message
 * can't produce a huge capture group; the map lookup bounds cardinality anyway.
 */
const LEGACY_PREFIX_RE = /^\[([^\]]{1,40})\]/;

/**
 * Derive the `event` value for an untyped legacy log call.
 *
 * Returns a stable, low-cardinality slug so composite index #2 gives coarse but
 * real queryability over the un-migrated call sites: "every camera-related row
 * in window Y" becomes one indexed query instead of a paginated sweep.
 */
export function deriveLegacyEvent(message: string): string {
  if (!message) return LEGACY_OTHER_EVENT;
  const match = message.match(LEGACY_PREFIX_RE);
  if (!match) return LEGACY_OTHER_EVENT;
  const mapped = LEGACY_PREFIX_EVENTS[match[1]];
  return mapped || LEGACY_OTHER_EVENT;
}
